import { Tooltip } from '@material-ui/core';
import CachedIcon from '@material-ui/icons/Cached';
import HourglassEmptyIcon from '@material-ui/icons/HourglassEmpty';
import DescriptionIcon from '@material-ui/icons/Description';
import format from 'date-fns/format';
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import BootstrapTable from 'react-bootstrap-table-next';
import { toast } from 'react-toastify';
import Loading from '../../../components/Loading';
import DownloadLoadingPopup from '../../../components/DownloadLoadingPopup';
import api from '../../../services/api';
import fontSize from '../../../styles/settings/fontSize';
import * as S from './styles';

/** Primeiros bytes de um PDF válido: %PDF */
function bufferLooksLikePdf(arrayBuffer) {
  if (!arrayBuffer || arrayBuffer.byteLength < 4) return false;
  const u8 = new Uint8Array(arrayBuffer, 0, 4);
  return u8[0] === 0x25 && u8[1] === 0x50 && u8[2] === 0x44 && u8[3] === 0x46;
}

function parseFilenameFromContentDisposition(header) {
  if (!header || typeof header !== 'string') return null;
  const utf8 = header.match(/filename\*=UTF-8''([^;\n]+)/i);
  if (utf8?.[1]) {
    try {
      return decodeURIComponent(utf8[1].trim().replace(/['"]/g, ''));
    } catch {
      return utf8[1].trim().replace(/['"]/g, '');
    }
  }
  const simple = header.match(/filename="([^"]+)"/i) || header.match(/filename=([^;\n]+)/i);
  if (simple?.[1]) return simple[1].trim().replace(/['"]/g, '');
  return null;
}

function toastFirstApiMessage(json) {
  const r = json?.resposta;
  if (Array.isArray(r) && r[0]?.message) {
    toast.error(String(r[0].message));
    return;
  }
  if (json?.notifications?.[0]?.mensagem) {
    toast.error(String(json.notifications[0].mensagem));
    return;
  }
  toast.error('Não foi possível baixar o PDF.');
}

/**
 * Download de PDF com responseType arraybuffer (evita corrupção por tratamento do axios como texto/UTF-8).
 */
async function downloadPdfFromApiResponse(response, fallbackName) {
  const buffer = response?.data;
  if (!(buffer instanceof ArrayBuffer)) {
    toast.error('Resposta inválida do servidor ao baixar o PDF.');
    return;
  }

  const ct = String(response.headers?.['content-type'] || '').toLowerCase();
  const firstByte = buffer.byteLength ? new Uint8Array(buffer, 0, 1)[0] : 0;

  if (ct.includes('application/json') || firstByte === 0x7b) {
    try {
      const text = new TextDecoder('utf-8').decode(buffer);
      const json = JSON.parse(text);
      toastFirstApiMessage(json);
    } catch {
      toast.error('Não foi possível baixar o PDF.');
    }
    return;
  }

  if (!bufferLooksLikePdf(buffer)) {
    try {
      const text = new TextDecoder('utf-8').decode(buffer);
      const json = JSON.parse(text);
      toastFirstApiMessage(json);
    } catch {
      toast.error('O arquivo recebido não é um PDF válido.');
    }
    return;
  }

  const fileName =
    response.headers?.['x-download-file-name'] ||
    response.headers?.['X-Download-FileName'] ||
    parseFilenameFromContentDisposition(response.headers?.['content-disposition']) ||
    fallbackName ||
    'documento.pdf';

  const blob = new Blob([buffer], { type: 'application/pdf' });
  const url = globalThis.URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = fileName;
  a.click();
  globalThis.URL.revokeObjectURL(url);
}

function normalizeItensAssinadasDia(resposta) {
  if (!resposta) return [];
  if (Array.isArray(resposta)) return resposta;
  return resposta.itens ?? resposta.Itens ?? [];
}

function formatarDataAssinatura(data) {
  if (!data) return null;
  const dataConvertida = new Date(data);
  if (Number.isNaN(dataConvertida.getTime())) return null;
  return format(dataConvertida, 'dd/MM/yyyy HH:mm');
}

/**
 * Mesmo layout de Histórico / Notas: TableContent, TableTitleHeader (sanMarino), BootstrapTable.
 * Sem fornecedor ou com proposta ativa: apenas a faixa azul do título (como Histórico/Notas).
 * A lista é filtrada pela data escolhida (enviada como dataAntecipacao na API).
 */
function SignedContractsPanel({
  antecipacaoId,
  fornecedorId,
  razaoSocialFornecedor,
  ancoraId,
  destinoPagamentoId,
  maxHeight,
  statusPropose,
}) {
  const [rows, setRows] = useState([]);
  const [isLoading, setLoading] = useState(false);
  const [downloadingTermoId, setDownloadingTermoId] = useState(null);
  const [masterStatusLoading, setMasterStatusLoading] = useState(false);
  const [masterStatus, setMasterStatus] = useState({
    assinado: false,
    dataAssinatura: null,
  });
  const destinoEfetivo =
    Number(destinoPagamentoId) > 0
      ? Number(destinoPagamentoId)
      : Number(sessionStorage.getItem('DestinoPagamentoIdAtual') || 0);

  const fetchLista = useCallback(async () => {
    const antId = Number(antecipacaoId);
    const fid = Number(fornecedorId);
    const aid = Number(ancoraId);
    if (!antId || !fid || !aid || !destinoEfetivo) {
      setRows([]);
      return;
    }

    setLoading(true);
    try {
      const res = await api.get('OperacoesAntecipacoes/ConsultarAntecipacoesAssinadasDiaOperacao', {
        params: {
          ancoraId: aid,
          destinoPagamentoId: destinoEfetivo,
          fornecedorId: fid,
          antecipacaoId: antId,
        },
      });

      if (res?.data?.status === 'Sucesso') {
        const lista = normalizeItensAssinadasDia(res.data.resposta).map((r) => ({
          ...r,
          antecipacaoId: r.antecipacaoId ?? r.AntecipacaoId,
          fornecedorId: r.fornecedorId ?? r.FornecedorId,
          razaoSocial: r.razaoSocial ?? r.RazaoSocial ?? '',
          destinoPagamento: r.destinoPagamento ?? r.DestinoPagamento ?? '',
          dataBase: r.dataBase ?? r.DataBase,
          caminhoPdfTermoAssinado: r.caminhoPdfTermoAssinado ?? r.CaminhoPdfTermoAssinado,
          fornecedorContratoMasterId:
            r.fornecedorContratoMasterId ?? r.FornecedorContratoMasterId ?? null,
          contratoMasterAssinado: Number(r.fornecedorContratoMasterId ?? r.FornecedorContratoMasterId ?? 0) > 0,
          nomeContratoMaster: r.nomeContratoMaster ?? r.NomeContratoMaster ?? '',
          caminhoPdfContratoMaster: r.caminhoPdfContratoMaster ?? r.CaminhoPdfContratoMaster,
          envelopeClicksignId: r.envelopeClicksignId ?? r.EnvelopeClicksignId ?? '',
        }));
        // Regra de negócio da tela: mostra somente arquivos da antecipação selecionada.
        setRows(lista.filter((item) => Number(item.antecipacaoId) === antId));
      } else {
        setRows([]);
      }
    } catch (error) {
      setRows([]);
      toast.error('Não foi possível carregar os termos de antecipação assinados da antecipação selecionada.');
    } finally {
      setLoading(false);
    }
  }, [antecipacaoId, fornecedorId, ancoraId, destinoEfetivo]);

  const handleDownloadTermo = useCallback(
    async (row) => {
      const antId = row?.antecipacaoId;
      const fid = row?.fornecedorId ?? fornecedorId;
      if (!antId) {
        toast.warning('Antecipação sem identificador.');
        return;
      }
      setDownloadingTermoId(antId);
      try {
        const response = await api.get('OperacoesAntecipacoes/BaixarPdfTermoAntecipacao', {
          params: {
            antecipacaoId: antId,
            fornecedorId: fid,
            ancoraId: ancoraId ?? 0,
          },
          responseType: 'arraybuffer',
          skipGlobalErrorHandler: true,
        });
        await downloadPdfFromApiResponse(response, `termo-antecipacao-${antId}.pdf`);
      } catch (error) {
        const data = error?.response?.data;
        if (data instanceof ArrayBuffer && data.byteLength && new Uint8Array(data, 0, 1)[0] === 0x7b) {
          try {
            const json = JSON.parse(new TextDecoder('utf-8').decode(data));
            toastFirstApiMessage(json);
          } catch {
            toast.error('Não foi possível baixar o termo de antecipação.');
          }
        } else {
          toast.error('Não foi possível baixar o termo de antecipação.');
        }
      } finally {
        setDownloadingTermoId(null);
      }
    },
    [fornecedorId, ancoraId],
  );

  const handleVerifyStatus = useCallback(
    async (row) => {
      try {
        const response = await api.get('OperacoesAntecipacoes/VerificarStatusClicksignContratoMaster', {
          params: {
            ancoraId: ancoraId ?? 0,
            antecipacaoId: row?.antecipacaoId ?? 0,
            fornecedorId: row?.fornecedorId ?? fornecedorId,
            envelopeClicksignId: row?.envelopeClicksignId || '',
          },
        });

        if (response?.data?.status === 'Sucesso') {
          toast.success(response?.data?.resposta?.message || 'Status atualizado com sucesso.');
          await fetchLista();
        }
      } catch (error) {
        toast.error('Não foi possível verificar o status do Clicksign.');
      }
    },
    [fornecedorId, ancoraId, fetchLista],
  );

  const fetchMasterStatus = useCallback(async () => {
    const fid = Number(fornecedorId);
    if (!fid) {
      setMasterStatus({ assinado: false, dataAssinatura: null });
      return;
    }

    setMasterStatusLoading(true);
    try {
      const response = await api.get('OperacoesAntecipacoes/ConsultarContratosMastersAssinados', {
        params: {
          ancoraId: Number(ancoraId) || 0,
          antecipacaoId: Number(antecipacaoId) || 0,
          fornecedorId: fid,
        },
      });

      if (response?.data?.status === 'Sucesso') {
        const itens = response?.data?.resposta?.itens || [];
        const itensAssinados = itens.filter(
          (item) => item && (item.statusAssinatura === 2 || item.statusAssinatura === 'Assinado' || item.dataAssinatura),
        );

        if (itensAssinados.length > 0) {
          const ultimo = itensAssinados
            .slice()
            .sort((a, b) => new Date(b?.dataAssinatura || 0) - new Date(a?.dataAssinatura || 0))[0];

          setMasterStatus({
            assinado: true,
            dataAssinatura: ultimo?.dataAssinatura || null,
          });
        } else {
          setMasterStatus({ assinado: false, dataAssinatura: null });
        }
      } else {
        setMasterStatus({ assinado: false, dataAssinatura: null });
      }
    } catch (error) {
      console.error('Erro ao consultar status do contrato master:', error);
      setMasterStatus({ assinado: false, dataAssinatura: null });
    } finally {
      setMasterStatusLoading(false);
    }
  }, [fornecedorId, ancoraId, antecipacaoId]);

  const iconSize = Number(String(fontSize.tableRowText).replace('px', '')) || 14;

  const columns = useMemo(
    () => [
      {
        text: 'Antecipação',
        dataField: 'antecipacaoId',
        sort: true,
        formatter: (cell) => <span className="text-center-block">{cell}</span>,
      },
      {
        text: 'Fornecedor',
        dataField: 'razaoSocial',
        sort: true,
        formatter: (cell) => <span className="text-left-block">{cell}</span>,
      },
      {
        text: 'Destino',
        dataField: 'destinoPagamento',
        sort: true,
        formatter: (cell) => <span className="text-left-block">{cell}</span>,
      },
      {
        text: 'Data',
        dataField: 'dataBase',
        sort: true,
        formatter: (cell) => {
          if (!cell) return '';
          try {
            return <span className="text-center-block">{format(new Date(cell), 'dd/MM/yyyy')}</span>;
          } catch (e) {
            return <span className="text-center-block">{String(cell)}</span>;
          }
        },
      },
      {
        text: 'Status termo',
        dataField: 'statusTermo',
        sort: false,
        formatter: () => <span className="text-center-block">Assinado</span>,
      },
      {
        text: 'Contrato master',
        dataField: 'nomeContratoMaster',
        sort: true,
        formatter: (cell, row) =>
          row?.contratoMasterAssinado ? (
            <span className="text-left-block">{cell || 'Sim'}</span>
          ) : (
            <span className="text-center-block">Não assinado</span>
          ),
      },
      {
        text: 'Ações',
        dataField: 'acoes',
        sort: false,
        formatter: (cell, row) => {
          const termoAssinado = !!row?.caminhoPdfTermoAssinado;
          const podeVerificar = !termoAssinado && !!row?.envelopeClicksignId;
          const isDownloadingTermo = downloadingTermoId === row?.antecipacaoId;
          return (
            <span className="text-center-block" style={{ display: 'inline-flex', gap: 6, alignItems: 'center' }}>
              {termoAssinado ? (
                <Tooltip placement="top" title="Baixar termo de antecipação assinado">
                  <button
                    type="button"
                    className="pointer action-btn"
                    disabled={isDownloadingTermo}
                    onClick={(e) => {
                      e.stopPropagation();
                      handleDownloadTermo(row);
                    }}
                    style={{
                      background: 'transparent',
                      border: 'none',
                      padding: 0,
                      cursor: isDownloadingTermo ? 'not-allowed' : 'pointer',
                      opacity: isDownloadingTermo ? 0.65 : 1,
                    }}
                  >
                    {isDownloadingTermo ? (
                      <HourglassEmptyIcon style={{ fontSize: iconSize + 10, color: '#FB8832' }} />
                    ) : (
                      <DescriptionIcon style={{ fontSize: iconSize + 10, color: '#232D64' }} />
                    )}
                  </button>
                </Tooltip>
              ) : (
                <Tooltip placement="top" title="Aguardando assinatura do termo">
                  <span style={{ opacity: 0.5 }}>—</span>
                </Tooltip>
              )}
              {podeVerificar ? (
                <Tooltip placement="top" title="Verificar status Clicksign">
                  <button
                    type="button"
                    className="pointer action-btn"
                    disabled={isDownloadingTermo}
                    onClick={(e) => {
                      e.stopPropagation();
                      handleVerifyStatus(row);
                    }}
                    style={{
                      background: 'transparent',
                      border: 'none',
                      padding: 0,
                      opacity: isDownloadingTermo ? 0.5 : 1,
                      cursor: isDownloadingTermo ? 'not-allowed' : 'pointer',
                    }}
                  >
                    <HourglassEmptyIcon style={{ fontSize: iconSize + 10, color: '#FB8832' }} />
                  </button>
                </Tooltip>
              ) : null}
            </span>
          );
        },
      },
    ],
    [handleDownloadTermo, handleVerifyStatus, iconSize, downloadingTermoId],
  );

  useEffect(() => {
    fetchLista();
  }, [fetchLista]);

  useEffect(() => {
    fetchMasterStatus();
  }, [fetchMasterStatus]);

  const dataAssinaturaFormatada = formatarDataAssinatura(masterStatus.dataAssinatura);
  const textoStatusContratoMaster = masterStatusLoading
    ? 'Consultando...'
    : masterStatus.assinado
      ? `Assinado${dataAssinaturaFormatada ? ` em ${dataAssinaturaFormatada}` : ''}`
      : 'Não assinado';

  if (statusPropose || !fornecedorId) {
    return (
      <S.TableTitleHeader>
        <h3 className="operation-title">Termos de antecipação assinados</h3>
      </S.TableTitleHeader>
    );
  }

  return (
    <>
      <S.TableContent>
        <S.TableTitleHeader>
          <h3 className="operation-title">Termos de antecipação assinados</h3>
          <Tooltip placement="top" title="Recarregar conteúdo">
            <CachedIcon className="pointer" onClick={() => fetchLista()} />
          </Tooltip>
        </S.TableTitleHeader>
        <div
          style={{
            display: 'flex',
            flexWrap: 'nowrap',
            alignItems: 'center',
            gap: 24,
            margin: '0 0 10px 4px',
            fontSize: fontSize.messageParagraph,
            color: '#424242',
            position: 'relative',
            zIndex: 0,
          }}
        >
          <span>
            Fornecedor: <strong>{razaoSocialFornecedor || '—'}</strong>
          </span>
          <span>
            Antecipação: <strong>{antecipacaoId || '—'}</strong>
          </span>
          <span>
            Contrato Master:{' '}
            <strong>{textoStatusContratoMaster}</strong>
          </span>
        </div>
        {isLoading ? (
          <Loading />
        ) : (
          <S.HeightTableContainer maxHeight={maxHeight}>
            <BootstrapTable
              columns={columns}
              data={rows}
              keyField="antecipacaoId"
              striped
              hover
              condensed
              noDataIndication={() =>
                'Nenhum arquivo assinado encontrado para a antecipação selecionada.'
              }
            />
          </S.HeightTableContainer>
        )}
      </S.TableContent>
      <DownloadLoadingPopup
        open={Boolean(downloadingTermoId)}
        message="Baixando termo de antecipação assinado..."
      />
    </>
  );
}

export default SignedContractsPanel;
